/**
 * One studio (container, D32): its devices and settings read structurally,
 * its mapping table judged against a show style (the studio's default first)
 * with each mapping editable, and the config edited as JSON — checked while
 * typing with the engine's schema (`lib/studio/config.ts`), saved with
 * optimistic concurrency.
 *
 * Live and dirty-aware (D35, `useLiveResource`): someone else's save refreshes
 * the page in place while it has no unsaved edits; with unsaved edits nothing
 * is replaced, and a banner offers "Overwrite with mine" or "Discard mine and
 * load theirs" — as does a save that answers 409. A delete by someone else is
 * a banner too; the edits stay.
 *
 * The editor's text, name, site and default show style are this page's state
 * (the draft), made from the live base. A mapping-table edit rewrites the text.
 */
import type { Studio } from '@acp/api-contract/studios.js';
import { ApiError } from '@acp/api-client/client.js';
import { parseShowStyleConfig } from '@acp/engine/showStyle.js';
import { useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { StudioConflict } from '../api/studios.js';
import { type LiveResource, useDeleteStudio, useGrants, useLiveResource, usePlaylists, useProductions, useRundowns, useSaveStudio, useServices, useShowStyle, useShowStyles, useStudio } from '../app/hooks.js';
import { DeleteStudioDialog } from '../components/studios/DeleteStudioDialog.js';
import { sourceLabel } from './ProductionsView.js';
import { LiveUpdateBanner } from '../components/LiveUpdateBanner.js';
import { StudioConfigEditor } from '../components/studios/StudioConfigEditor.js';
import { StudioConfigSummary } from '../components/studios/StudioConfigSummary.js';
import { StudioMappingTable } from '../components/studios/StudioMappingTable.js';
import { Button } from '../components/ui/button.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card.js';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs.js';
import { whenText } from '../lib/live/whenText.js';
import { allows } from '../lib/permissions.js';
import { shownShowStyle } from '../lib/showStyle/pick.js';
import { checkConfigText, type ConfigProblem, configText, serverProblems, studioSummary } from '../lib/studio/config.js';
import { mappingRows, pairProblems, withMapping } from '../lib/studio/mappings.js';

function messageOf(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

type Draft = { readonly name: string; readonly siteId: string; readonly defaultShowStyleId: string; readonly text: string };

function draftOf(studio: Studio): Draft {
  return { name: studio.name, siteId: studio.siteId ?? '', defaultShowStyleId: studio.defaultShowStyleId ?? '', text: configText(studio.config) };
}

export function StudioView({ studioId }: { readonly studioId: string }) {
  const studio = useStudio(studioId);
  // Data first: a background refetch that fails must not replace the editor (and the user's unsaved edits).
  if (studio.data !== undefined) return <StudioEditorPage key={studio.data.studioId} latest={studio.data} />;
  if (studio.error !== null) {
    return (
      <div className="mx-auto grid max-w-xl gap-4" role="alert" data-testid="studio-failed">
        <p className="text-sm text-destructive">Could not load the studio: {messageOf(studio.error)}</p>
        <Button variant="outline" className="w-fit" onClick={() => void studio.refetch()}>
          Retry
        </Button>
      </div>
    );
  }
  return <p className="p-8 text-center text-sm text-muted-foreground">Loading the studio…</p>;
}

/** The mapping table's rows and problems for the draft against the judged show style, or why there are none. */
function useMappingTable(check: ReturnType<typeof checkConfigText>, judgeId: string) {
  const showStyle = useShowStyle(judgeId === '' ? null : judgeId);
  return useMemo(() => {
    if (judgeId === '') return { rows: null, problems: [], notice: 'Pick a show style to see which of its layers this studio maps.' };
    if (showStyle.data === undefined) return { rows: null, problems: [], notice: showStyle.error === null ? 'Loading the show style…' : `Could not load the show style: ${messageOf(showStyle.error)}` };
    if (!check.ok) return { rows: null, problems: [], notice: 'The studio config does not parse; fix it in the JSON tab.' };
    const parsed = parseShowStyleConfig(showStyle.data.config);
    if (!parsed.ok) return { rows: null, problems: [], notice: 'The show style does not parse with this build’s schema.' };
    return { rows: mappingRows(parsed.config, check.config), problems: pairProblems(parsed.config, check.config), notice: null };
  }, [judgeId, showStyle.data, showStyle.error, check]);
}

/** Whether the draft differs from the version it was made from. */
function isDirty(draft: Draft, base: Studio): boolean {
  return draft.text !== configText(base.config) || draft.name !== base.name || draft.siteId !== (base.siteId ?? '') || draft.defaultShowStyleId !== (base.defaultShowStyleId ?? '');
}

/** '' (none) as null. */
function orNull(text: string): string | null {
  return text.trim() === '' ? null : text.trim();
}

/** The draft's save and what a failed one leaves: the server's issues (a 409 goes to the live banner). */
function useStudioSave(draft: Draft, live: LiveResource<Studio>, check: ReturnType<typeof checkConfigText>) {
  const save = useSaveStudio();
  const [serverIssues, setServerIssues] = useState<readonly ConfigProblem[] | null>(null);
  const submit = (version: number): void => {
    if (!check.ok) return;
    setServerIssues(null);
    const request = { studioId: live.base.studioId, version, name: draft.name.trim(), siteId: orNull(draft.siteId), defaultShowStyleId: orNull(draft.defaultShowStyleId), config: check.document };
    live.beginSave();
    save.mutate(request, {
      onSuccess: (saved) => {
        live.saved(saved);
        toast.success(`Saved version ${String(saved.version)}`);
      },
      onError: (err) => {
        if (err instanceof StudioConflict) live.conflict(err.current);
        else {
          live.saveFailed();
          if (err instanceof ApiError && err.code === 'validation-failed') setServerIssues(serverProblems(err.issues));
          else toast.error(`Not saved: ${messageOf(err)}`);
        }
      },
    });
  };
  return { submit, pending: save.isPending, serverIssues, setServerIssues };
}

function StudioEditorPage({ latest }: { readonly latest: Studio }) {
  const grants = useGrants();
  const showStyles = useShowStyles();
  const { nowMs } = useServices();
  const [loaded] = useState(latest);
  const [draft, setDraft] = useState<Draft>(() => draftOf(loaded));
  const live = useLiveResource<Studio>({
    kind: 'studio',
    id: loaded.studioId,
    loaded,
    latest,
    isDirty: (base) => isDirty(draft, base),
    versionOf: (s) => s.version,
    authorOf: (s) => s.updatedBy,
    writtenAtOf: (s) => Date.parse(s.updatedAt),
    onReset: (s) => setDraft(draftOf(s)),
  });
  const base = live.base;
  const check = useMemo(() => checkConfigText(draft.text), [draft.text]);
  const saving = useStudioSave(draft, live, check);
  const choices = (showStyles.data ?? []).map((s) => ({ id: s.showStyleId, name: s.name }));
  const canSave = allows(grants, 'device.configure', { siteId: base.siteId, studioId: base.studioId });
  const dirty = isDirty(draft, base);
  // The summary shows the draft while it is valid, else the saved version.
  const savedCheck = useMemo(() => checkConfigText(configText(base.config)), [base]);
  const shown = check.ok ? check : savedCheck;
  const notice = live.notice;

  return (
    <div className="mx-auto grid max-w-6xl gap-6" data-testid="studio-page">
      {notice === null ? null : (
        <LiveUpdateBanner
          notice={{ kind: notice.kind, who: notice.by, at: whenText(notice.atMs, nowMs()), what: 'studio' }}
          pending={saving.pending}
          canSave={canSave && check.ok}
          onOverwrite={() => {
            const theirs = live.keepMine();
            if (theirs !== null) saving.submit(theirs.version);
          }}
          onDiscard={() => {
            saving.setServerIssues(null);
            live.discardMine();
          }}
        />
      )}
      <Card className="relative" data-testid="studio-card" data-refreshes={live.refreshes}>
        {live.refreshes > 0 ? <span key={live.refreshes} aria-hidden="true" className="pointer-events-none absolute inset-0 animate-live-flash rounded-xl ring-2 ring-success" data-testid="live-flash" /> : null}
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div className="grid gap-1.5">
            <CardTitle>{base.name}</CardTitle>
            <CardDescription>
              <span className="font-mono">{base.studioId}</span> · version {base.version} · last written by {base.updatedBy}
              {dirty ? ' · unsaved changes' : ''}
            </CardDescription>
          </div>
          {canSave ? <StudioDelete studio={base} /> : null}
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="overview">
            <TabsList>
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="mappings" data-testid="studio-mappings-tab">
                Mappings
              </TabsTrigger>
              <TabsTrigger value="json" data-testid="studio-json-tab">
                Config (JSON)
              </TabsTrigger>
            </TabsList>
            <TabsContent value="overview" className="pt-4">
              {shown.ok ? <StudioConfigSummary summary={studioSummary(shown.config)} /> : <p className="text-sm text-muted-foreground">The stored config does not parse with this build’s schema; see the JSON.</p>}
            </TabsContent>
            <TabsContent value="mappings" className="grid gap-4 pt-4">
              <MappingsPanel draft={draft} base={base} check={check} choices={choices} canSave={canSave} dirty={dirty} pending={saving.pending} onDraft={setDraft} onSave={() => saving.submit(base.version)} />
            </TabsContent>
            <TabsContent value="json" className="pt-4">
              <StudioConfigEditor
                name={draft.name}
                siteId={draft.siteId}
                defaultShowStyleId={draft.defaultShowStyleId}
                showStyles={choices}
                text={draft.text}
                problems={check.ok ? (saving.serverIssues ?? []) : check.problems}
                problemSource={check.ok && saving.serverIssues !== null ? 'server' : 'editor'}
                version={base.version}
                dirty={dirty}
                canSave={canSave}
                pending={saving.pending}
                onName={(name) => setDraft({ ...draft, name })}
                onSite={(siteId) => setDraft({ ...draft, siteId })}
                onDefaultShowStyle={(defaultShowStyleId) => setDraft({ ...draft, defaultShowStyleId })}
                onText={(text) => {
                  saving.setServerIssues(null);
                  setDraft({ ...draft, text });
                }}
                onSave={() => saving.submit(base.version)}
                onRevert={() => {
                  saving.setServerIssues(null);
                  setDraft(draftOf(base));
                }}
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}

/** The mapping tab: the table against the judged show style, each edit rewriting the draft's text, and save/revert. */
function MappingsPanel(props: {
  readonly draft: Draft;
  readonly base: Studio;
  readonly check: ReturnType<typeof checkConfigText>;
  readonly choices: readonly { readonly id: string; readonly name: string }[];
  readonly canSave: boolean;
  readonly dirty: boolean;
  readonly pending: boolean;
  readonly onDraft: (draft: Draft) => void;
  readonly onSave: () => void;
}) {
  const { draft, base, check, choices } = props;
  const [pickedJudge, setJudge] = useState('');
  const studioDefault = orNull(draft.defaultShowStyleId);
  const judgeId = shownShowStyle(pickedJudge, studioDefault, choices);
  const table = useMappingTable(check, judgeId);
  return (
    <>
      <StudioMappingTable
        showStyles={choices}
        showStyleId={judgeId}
        studioDefault={studioDefault}
        rows={table.rows}
        notice={table.notice}
        problems={table.problems}
        canEdit={props.canSave}
        onShowStyle={setJudge}
        onMap={(layer, edit) => {
          if (check.ok) props.onDraft({ ...draft, text: configText(withMapping(check.document, layer, edit)) });
        }}
      />
      {props.canSave ? (
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Mapping edits change the studio config; save them here or from the JSON tab.</span>
          <Button variant="outline" className="ml-auto" onClick={() => props.onDraft(draftOf(base))} disabled={!props.dirty || props.pending}>
            Revert
          </Button>
          <Button onClick={props.onSave} disabled={!props.dirty || !check.ok || props.pending} data-testid="save-studio-mappings">
            {props.pending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      ) : null}
    </>
  );
}

/**
 * Delete the studio (wave 7b-tail C): confirmed by typing its name, naming the productions made from it that have
 * not ended (they keep running from their own snapshot, D22 — the API has no in-use guard), from the version shown.
 */
function StudioDelete({ studio }: { readonly studio: Studio }) {
  const productions = useProductions();
  const rundowns = useRundowns();
  const playlists = usePlaylists();
  const remove = useDeleteStudio();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const names = {
    rundowns: new Map((rundowns.data ?? []).map((r) => [r.rundownId, r.name])),
    playlists: new Map((playlists.data ?? []).map((p) => [p.playlistId, p.name])),
  };
  const inUse = (productions.data ?? []).filter((p) => p.studioId === studio.studioId && p.status !== 'ended').map((p) => ({ productionId: p.productionId, label: sourceLabel(p, names.rundowns, names.playlists) }));
  return (
    <>
      <Button
        variant="outline"
        className="text-destructive"
        onClick={() => {
          remove.reset();
          setOpen(true);
        }}
        data-testid="delete-studio"
      >
        Delete studio
      </Button>
      <DeleteStudioDialog
        open={open}
        studioName={studio.name}
        inUse={inUse}
        pending={remove.isPending}
        error={remove.error === null ? null : messageOf(remove.error)}
        onConfirm={() =>
          remove.mutate(
            { studioId: studio.studioId, version: studio.version },
            {
              onSuccess: (deleted) => {
                setOpen(false);
                toast.success(`Studio ${deleted.name} deleted`);
                void navigate({ to: '/studios' });
              },
            },
          )
        }
        onOpenChange={setOpen}
      />
    </>
  );
}
